import React, {useState, useEffect, useMemo, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {Button, CalendarPicker, Card, Skeleton, ProgressBar, InfiniteScroll, Dialog} from 'antd-mobile';
import PullToRefreshWrapper from '../../../components/PullToRefreshWrapper';
import {DownOutline, ExclamationCircleOutline, RightOutline, CheckOutline, QuestionCircleOutline} from 'antd-mobile-icons';
import DspLayout from "../layouts/DspLayout";
import {formatLocalizedDate} from '../../../utils/dateFormatUtils';
import {DspDriverOperationalData, SortOption} from '../graphql/queries/dspOperationalData';
import {useDspOperationalData} from '../../../hooks/useDspOperationalData';
import {calculateLongStayLabel} from '../../../utils/dspOperationalDataUtils';


const DspOperationalDataPage: React.FC = () => {
    const {t} = useTranslation();
    const [currentTime, setCurrentTime] = useState<Date>(new Date());
    const sortSelectorTriggerRef = useRef<HTMLDivElement>(null);
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

    // 使用自定义Hook
    const {
        selectedDate,
        showCalendar,
        setShowCalendar,
        sortBy,
        showSortSelector,
        setShowSortSelector,
        summaryData,
        summaryLoading,
        driversLoading,
        sortedDriversData,
        totalDrivers,
        hasMore,
        currentPage,
        scrollContainerRef,
        handleDateSelect,
        handleSort,
        handleRefresh,
        loadMore,
        handleViewDetails,
        handleViewDriverPackages
    } = useDspOperationalData();

    // 定时更新当前时间，用于实时计算停留时长
    useEffect(() => {
        // 页面加载时输出当前时间
        const now = new Date();
        console.log('[DspOperationalDataPage] 页面刷新，当前时间:', now.toISOString(), '本地时间:', now.toLocaleString());
        setCurrentTime(now);

        const interval = setInterval(() => {
            const newTime = new Date();
            setCurrentTime(newTime);
        }, 60000); // 每分钟更新一次

        return () => clearInterval(interval);
    }, []);

    // 计算30天前的日期作为最小可选日期
    const minDate = useMemo(() => {
        const date = new Date();
        date.setDate(date.getDate() - 30);
        return date;
    }, []);

    // 排序选项配置
    const sortOptions = [
        {label: t('pages.dsp.operationalData.deliverySuccessRate'), value: SortOption.DELIVERY_RATE},
        {label: t('pages.dsp.operationalData.pickedUp'), value: SortOption.RECEIVED_PACKAGES},
        {label: t('pages.dsp.operationalData.failedParcels'), value: SortOption.FAILED_PACKAGES}
    ];

    // 点击外部关闭排序选择器
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (showSortSelector && !target.closest('.sort-selector-container')) {
                setShowSortSelector(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showSortSelector]);

    // 确保滚动容器的高度正确计算
    useEffect(() => {
        const updateScrollContainerHeight = () => {
            if (scrollContainerRef.current) {
                const container = scrollContainerRef.current;
                // 找到最外层的容器（DspLayout 的内容区域）
                const rootContainer = container.closest('.h-full.flex.flex-col');
                const pullToRefreshContent = document.querySelector('.adm-pull-to-refresh-content') as HTMLElement;
                
                if (rootContainer && pullToRefreshContent) {
                    const rootHeight = rootContainer.clientHeight;
                    const fixedAreaHeight = pullToRefreshContent.offsetHeight;
                    const scrollContainerHeight = rootHeight - fixedAreaHeight;
                    
                    if (scrollContainerHeight > 0) {
                        container.style.height = `${scrollContainerHeight}px`;
                    }
                }
            }
        };

        // 初始设置
        const timeoutId = setTimeout(updateScrollContainerHeight, 100);

        // 监听窗口大小变化
        window.addEventListener('resize', updateScrollContainerHeight);

        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('resize', updateScrollContainerHeight);
        };
    }, [sortedDriversData]);

    // 防止 adm-pull-to-refresh-content 区域滚动，只能下拉刷新
    // 但允许下拉菜单溢出显示
    useEffect(() => {
        const style = document.createElement('style');
        style.id = 'dsp-operational-data-fixed-style';
        style.textContent = `
            .adm-pull-to-refresh-content {
                overflow: hidden !important;
                position: relative !important;
                height: auto !important;
                touch-action: pan-y !important;
            }
            [data-progress-wrapper] .adm-progress-bar-text {
                display: none !important;
            }
            [data-progress-wrapper] .adm-progress-bar-fill {
                display: none !important;
            }
            [data-progress-wrapper] .adm-progress-bar-trail {
                width: 100% !important;
            }
        `;
        document.head.appendChild(style);
        
        return () => {
            const existingStyle = document.getElementById('dsp-operational-data-fixed-style');
            if (existingStyle) {
                document.head.removeChild(existingStyle);
            }
        };
    }, []);

    // 计算下拉菜单位置（仅在显示时计算一次）
    useEffect(() => {
        if (showSortSelector && sortSelectorTriggerRef.current) {
            const rect = sortSelectorTriggerRef.current.getBoundingClientRect();
            setDropdownStyle({
                position: 'fixed',
                top: `${rect.bottom + 4}px`,
                left: `${rect.left}px`,
                zIndex: 9999
            });
        }
    }, [showSortSelector]);

    return (
        <DspLayout title={t('pages.dsp.navigation.operationData')}>
            <div className="h-full flex flex-col bg-gray-50 overflow-hidden">
                <PullToRefreshWrapper onRefresh={handleRefresh}>
                    {/* 固定区域：日期选择器、提示文案、汇总数据、司机列表标题 */}
                    <div className="bg-gray-50" style={{ backgroundColor: '#f9fafb' }}>
                            {/* 日期选择器 - 移到左上角 */}
                            <div className="px-3 pt-3 pb-2 bg-gray-50">
                                <Button
                                    fill='none'
                                    onClick={() => setShowCalendar(true)}
                                    className="text-sm"
                                    style={{padding: '4px 8px', minWidth: 'auto'}}
                                >
                                    <div className="flex items-center">
                                        <span className="mr-1 text-gray-700">
                                            {formatLocalizedDate(selectedDate, {
                                                year: 'numeric',
                                                month: '2-digit',
                                                day: '2-digit'
                                            })}
                                        </span>
                                        <DownOutline className="text-gray-500" style={{fontSize: '12px'}}/>
                                    </div>
                                </Button>
                            </div>

                            <CalendarPicker
                                visible={showCalendar}
                                onClose={() => setShowCalendar(false)}
                                onConfirm={handleDateSelect}
                                defaultValue={selectedDate}
                                selectionMode="single"
                                min={minDate}
                                max={new Date()}
                                title={t('pages.delivery.delivered.selectDate')}
                            />

                            {/* 提示文案 */}
                            <div className="px-3 mb-3">
                                <div className="flex items-center text-sm text-gray-600 gap-0">
                                    <ExclamationCircleOutline style={{fontSize: '1rem', width: '1rem', height: '1rem', flexShrink: 0}}/>
                                    <span>{t('pages.dsp.operationalData.description')}</span>
                                </div>
                                {(() => {
                                    const calculatedAt = summaryData?.getDspOperationalSummary?.calculatedAt ?? 
                                        (sortedDriversData.length > 0 ? sortedDriversData[0].calculatedAt : null);
                                    return calculatedAt && (
                                        <div className="text-xs text-orange-500 mt-1 ml-4">
                                            {t('pages.dsp.operationalData.lastUpdated')}{' '}
                                            {new Date(calculatedAt * 1000).toLocaleString('zh-CN', {
                                                year: 'numeric',
                                                month: '2-digit',
                                                day: '2-digit',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                second: '2-digit',
                                                hour12: false
                                            }).replace(/\//g, '-')}
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* DSP汇总数据区域 - 固定在顶部 */}
                            <div className="px-3 mb-3">
                                <Card 
                                    className="rounded-lg shadow-sm cursor-pointer active:opacity-70"
                                    onClick={handleViewDetails}
                                >
                                    <div className="py-4 px-0">
                                        {summaryLoading ? (
                                            <Skeleton.Paragraph lineCount={3} animated/>
                                        ) : (
                                            <>
                                                <div className="grid grid-cols-4 gap-2 sm:gap-4 mb-2 sm:mb-4">
                                                    <div className="text-center">
                                                        <div className="text-xl sm:text-2xl font-bold text-gray-800">
                                                            {summaryData?.getDspOperationalSummary?.totalPackages ?? 0}
                                                        </div>
                                                        <div className="text-xs text-gray-500 leading-tight">
                                                            <span className="inline-flex items-center gap-0">
                                                                {t('pages.dsp.operationalData.scheduledParcels')}
                                                                <QuestionCircleOutline
                                                                    className="text-gray-400"
                                                                    style={{fontSize: '1rem', width: '1rem', height: '1rem', flexShrink: 0, cursor: 'pointer'}}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        Dialog.alert({
                                                                            content: t('pages.dsp.operationalData.scheduledParcelsTooltip'),
                                                                            confirmText: t('pages.dsp.packageScan.confirm'),
                                                                        });
                                                                    }}
                                                                />
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="text-center">
                                                        <div className="text-xl sm:text-2xl font-bold text-gray-800">
                                                            {Number(summaryData?.getDspOperationalSummary?.receivedPackages ?? 0) + Number(summaryData?.getDspOperationalSummary?.transferredIn ?? 0)}
                                                        </div>
                                                        <div className="text-xs text-gray-500 leading-tight">
                                                            <span className="inline-flex items-center gap-0">
                                                                {t('pages.dsp.operationalData.driverPickup')}
                                                                <QuestionCircleOutline
                                                                    className="text-gray-400"
                                                                    style={{fontSize: '1rem', width: '1rem', height: '1rem', flexShrink: 0, cursor: 'pointer'}}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        Dialog.alert({
                                                                            content: t('pages.dsp.operationalData.driverPickupTooltip'),
                                                                            confirmText: t('pages.dsp.packageScan.confirm'),
                                                                        });
                                                                    }}
                                                                />
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="text-center">
                                                        <div className="text-xl sm:text-2xl font-bold text-gray-800">
                                                            {summaryData?.getDspOperationalSummary?.deliveredPackages ?? 0}
                                                        </div>
                                                        <div className="text-xs text-gray-500 leading-tight">{t('pages.dsp.operationalData.delivered')}</div>
                                                    </div>
                                                    <div className="text-center">
                                                        <div className="text-xl sm:text-2xl font-bold text-purple-600">
                                                            {summaryData?.getDspOperationalSummary?.deliveryRate?.toFixed(2) ?? '0.00'}%
                                                        </div>
                                                        <div className="text-xs text-gray-500 leading-tight">{t('pages.dsp.operationalData.deliverySuccessRate')}</div>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-4 gap-2 sm:gap-4 pt-2 sm:pt-4">
                                                    <div className="text-center">
                                                        <div className="text-base sm:text-lg font-semibold text-gray-700">
                                                            {summaryData?.getDspOperationalSummary?.deliveringPackages ?? 0}
                                                        </div>
                                                        <div className="text-xs text-gray-500 leading-tight">{t('pages.dsp.operationalData.delivering')}</div>
                                                    </div>
                                                    <div className="text-center">
                                                        <div className="text-base sm:text-lg font-semibold text-gray-700">
                                                            {summaryData?.getDspOperationalSummary?.failedPackages ?? 0}
                                                        </div>
                                                        <div className="text-xs text-gray-500 leading-tight">{t('pages.dsp.operationalData.failed')}</div>
                                                    </div>
                                                    <div className="text-center">
                                                        <div className="text-base sm:text-lg font-semibold text-gray-700">
                                                            {summaryData?.getDspOperationalSummary?.returnedPackages ?? 0}
                                                        </div>
                                                        <div className="text-xs text-gray-500 leading-tight">{t('pages.dsp.operationalData.returnedToWarehouse')}</div>
                                                    </div>
                                                    <div className="flex justify-center mt-2 sm:mt-4">
                                                        <Button
                                                            size="small"
                                                            fill="none"
                                                            onClick={handleViewDetails}
                                                        >
                                                            <RightOutline/>
                                                        </Button>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </Card>
                            </div>

                            {/* 司机列表标题和排序选择器 - 固定 */}
                            <div className="px-3 mb-3">
                                <div className="flex items-center">
                                    <div className="text-sm font-medium text-gray-700 mr-2">
                                        {t('pages.dsp.operationalData.driver')}：{totalDrivers || 0}
                                    </div>
                                    <div className="relative sort-selector-container">
                                        <div 
                                            ref={sortSelectorTriggerRef}
                                            className="flex items-center cursor-pointer text-gray-700"
                                            onClick={() => setShowSortSelector(!showSortSelector)}
                                        >
                                            <DownOutline style={{fontSize: '16px'}}/>
                                        </div>

                                        {showSortSelector && (
                                            <div
                                                className="bg-white border border-gray-200 rounded-lg shadow-lg min-w-[140px]"
                                                style={dropdownStyle}
                                            >
                                                {sortOptions.map((option, index) => (
                                                    <div
                                                        key={option.value}
                                                        className={`flex items-center justify-between px-3 py-3 text-sm cursor-pointer hover:bg-gray-50
                                                             ${index === 0 ? 'rounded-t-lg' : ''} ${index === sortOptions.length - 1 ? 'rounded-b-lg' : ''}`}
                                                        onClick={() => handleSort(option.value)}
                                                    >
                                                        <span className={`${
                                                            sortBy === option.value ? 'text-purple-600 font-medium' : 'text-gray-700'
                                                        }`}>
                                                            {option.label}
                                                        </span>
                                                        {sortBy === option.value && (
                                                            <CheckOutline className="text-purple-600" style={{fontSize: '14px'}}/>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </PullToRefreshWrapper>

                    {/* 司机列表区域 - 可滚动容器 */}
                    <div className="flex-1 px-3 overflow-hidden">
                        <div 
                            ref={scrollContainerRef}
                            className="h-full overflow-y-auto"
                            style={{
                                WebkitOverflowScrolling: 'touch',
                                overflowX: 'hidden',
                                touchAction: 'pan-y',
                                overscrollBehavior: 'contain'
                            }}
                            onScroll={(e) => {
                                const target = e.currentTarget;
                                const scrollTop = target.scrollTop;
                                const scrollHeight = target.scrollHeight;
                                const clientHeight = target.clientHeight;
                                const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;
                                
                                if (isNearBottom && hasMore && !driversLoading) {
                                    loadMore();
                                }
                            }}
                        >
                            {driversLoading && sortedDriversData.length === 0 ? (
                                <div className="space-y-3">
                                    {[1, 2, 3].map(i => (
                                        <Card key={i} className="rounded-lg">
                                            <Skeleton.Paragraph lineCount={2} animated/>
                                        </Card>
                                    ))}
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {sortedDriversData.length === 0 ? (
                                        <div className="text-center py-8 text-gray-500">
                                            {t('pages.dsp.operationalData.noData')}
                                        </div>
                                    ) : (
                                        sortedDriversData.map((driver: DspDriverOperationalData, index: number) => {
                                            const itemNumber = index + 1;
                                            
                                            return (
                                                <Card 
                                                    key={driver.driverId} 
                                                    className="rounded-lg shadow-sm cursor-pointer active:opacity-70"
                                                    onClick={() => handleViewDriverPackages(driver.driverId)}
                                                >
                                                    <div className="p-4">
                                                        {/* 第一行：司机姓名（左对齐），已停留标签（右对齐） */}
                                                        <div className="flex items-start justify-between gap-2 mb-3">
                                                            <span className="font-medium text-gray-800">
                                                                {driver.driverName && driver.driverName.trim()
                                                                    ? driver.driverName
                                                                    : String(driver.driverId)}
                                                            </span>
                                                            {(() => {
                                                                const label = calculateLongStayLabel(
                                                                    driver.lastDeliveryTime,
                                                                    selectedDate,
                                                                    currentTime
                                                                );
                                                                return label && (
                                                                    <span
                                                                        className={`text-xs px-2 py-0.5 rounded whitespace-nowrap flex-shrink-0 ${
                                                                            label.color === 'red'
                                                                                ? 'bg-red-100 text-red-600'
                                                                                : 'bg-orange-100 text-orange-600'
                                                                        }`}
                                                                    >
                                                                        {label.text}
                                                                    </span>
                                                                );
                                                            })()}
                                                        </div>

                                                        {/* 第二行：索引图 + 司机取件（左对齐），查看包裹按钮（右对齐） */}
                                                        <div className="flex items-center justify-between mb-3">
                                                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                                                <div className="w-7 h-7 bg-gray-300 rounded-full flex items-center justify-center flex-shrink-0" style={{ marginLeft: '-14px' }}>
                                                                    <span className="text-xs font-medium text-gray-600">
                                                                        {itemNumber}
                                                                    </span>
                                                                </div>
                                                                <div>
                                                                    {t('pages.dsp.operationalData.driverPickup')}：{Number(driver.receivedPackages) + Number(driver.transferredIn || 0)}
                                                                </div>
                                                            </div>
                                                            <div className="flex-shrink-0">
                                                                <Button
                                                                    size="small"
                                                                    fill="outline"
                                                                    color="primary"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleViewDriverPackages(driver.driverId);
                                                                    }}
                                                                    className="text-xs rounded-full px-2 sm:px-3 whitespace-nowrap"
                                                                    style={{
                                                                        fontSize: '11px',
                                                                        height: '24px',
                                                                        borderRadius: '12px',
                                                                        minWidth: 'auto',
                                                                        maxWidth: '120px'
                                                                    }}
                                                                >
                                                                    {t('pages.dsp.operationalData.viewParcels')}
                                                                </Button>
                                                            </div>
                                                        </div>

                                                        {/* 第三行：进度条（左对齐），分数（右对齐） */}
                                                        <div className="flex items-center justify-between gap-4 mb-3">
                                                            <div 
                                                                className="relative flex-1 min-w-0"
                                                                data-progress-wrapper
                                                                style={{ maxWidth: 'calc(100% - 90px)' }}
                                                            >
                                                                <ProgressBar 
                                                                    percent={100}
                                                                    text={false}
                                                                    style={{
                                                                        '--fill-color': '#e5e7eb',
                                                                        '--track-color': '#e5e7eb',
                                                                        '--track-width': '15px',
                                                                        '--text-width': '0px'
                                                                    } as React.CSSProperties}
                                                                />
                                                                <div
                                                                    className="absolute top-1/2 left-0 -translate-y-1/2 h-[15px] rounded-[15px] z-[1] pointer-events-none"
                                                                    style={{
                                                                        width: `${Math.min(driver.deliveryRate, 100)}%`,
                                                                        backgroundColor: driver.deliveryRate === 0 ? '#D1D5DB' : '#10b981'
                                                                    }}
                                                                />
                                                                <div
                                                                    className={`absolute top-1/2 -translate-x-1/2 -translate-y-1/2 h-8 flex items-center justify-center shadow-sm z-10 border-2 border-white ${driver.deliveryRate % 1 !== 0 ? 'w-12 rounded-2xl' : 'w-8 rounded-full'} ${driver.deliveryRate === 0 ? 'bg-[#D1D5DB]' : 'bg-[#10b981]'}`}
                                                                    style={{
                                                                        left: `${Math.max(Math.min(driver.deliveryRate, 100), 0)}%`
                                                                    }}
                                                                >
                                                                    <span className="text-[10px] font-semibold text-white leading-none whitespace-nowrap">
                                                                        {Number(driver.deliveryRate ?? 0).toFixed(2)}%
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <span className="font-medium text-gray-800 text-sm whitespace-nowrap flex-shrink-0" style={{ minWidth: '80px', textAlign: 'right' }}>
                                                                {driver.deliveredPackages}/{Number(driver.totalPackages ?? 0)}
                                                            </span>
                                                        </div>

                                                        <div className="flex justify-between text-sm py-2">
                                                            <div className="flex flex-col items-start">
                                                                <span className="text-gray-600 mb-1 text-xs leading-tight">{t('pages.dsp.operationalData.delivered')}</span>
                                                                <span className="font-medium self-center">{driver.deliveredPackages}</span>
                                                            </div>
                                                            <div className="flex flex-col items-center">
                                                                <span className="text-gray-600 mb-1 text-xs leading-tight">{t('pages.dsp.operationalData.delivering')}</span>
                                                                <span className="font-medium">{driver.deliveringPackages}</span>
                                                            </div>
                                                            <div className="flex flex-col items-center">
                                                                <span className="text-gray-600 mb-1 text-xs leading-tight">{t('pages.dsp.operationalData.failed')}</span>
                                                                <span className="font-medium">{driver.failedPackages}</span>
                                                            </div>
                                                            <div className="flex flex-col items-center">
                                                                <span className="text-gray-600 mb-1 text-xs leading-tight">{t('pages.dsp.operationalData.returnedToWarehouse')}</span>
                                                                <span className="font-medium">{driver.returnedPackages}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </Card>
                                            );
                                        })
                                    )}
                                </div>
                            )}
                            
                            {/* 上拉加载更多 */}
                            <InfiniteScroll
                                loadMore={loadMore}
                                hasMore={hasMore}
                                threshold={100}
                            >
                                {driversLoading && currentPage > 0 && (
                                    <div className="text-center py-4 text-gray-500">
                                        {t('pages.dsp.operationalData.loading', '加载中...')}
                                    </div>
                                )}
                            </InfiniteScroll>
                        </div>
                    </div>
                </div>
            </DspLayout>
        );
    };

export default DspOperationalDataPage;


